import { toNumber } from 'lodash';

import { SearchHospitalService } from '@/services/hospital/SearchHospitalService';
import { HospitalSortType } from '@/types/bff/gql-schema';
import { FreeVaccinationSearchFilterType } from '@/types/bff/gql-schema';
import {
  BookingsMap,
  DoctorBookingTime,
  EnumCommonFilter,
  HospitalCommonFilterType,
  HospitalDoctorResponse,
  HospitalSearchCommonFilterMap,
  HospitalSearchMode,
  HospitalSearchSortType,
} from '@/types/hospital';
import { HospitalFreeInfluenzaType, HospitalInfluenzaFilter, InfluenzaFilterType } from '@/types/hospital';
import { parseQueryStringToArray } from '@/utils/url';

export const getHospitalSearchSortFilter = (sortType = ''): HospitalSortFilter => {
  switch (sortType) {
    case 'DISTANCE':
      return new HospitalSortFilter(HospitalSearchSortType.DISTANCE);
    case 'REVIEW_AVERAGE_RATING':
      return new HospitalSortFilter(HospitalSearchSortType.REVIEW_AVERAGE_RATING);
    case 'REVIEW_COUNT':
      return new HospitalSortFilter(HospitalSearchSortType.REVIEW_COUNT);
    case 'ACCURACY':
    default:
      return new HospitalSortFilter(HospitalSearchSortType.ACCURACY);
  }
};

export const getCoordinate = (queryLatitude?: string, queryLongitude?: string) => {
  if (!queryLatitude || !queryLongitude) {
    return SearchHospitalService.initializeUserCoordinate();
  }

  const latitude = toNumber(queryLatitude);
  const longitude = toNumber(queryLongitude);

  if (isNaN(latitude) || isNaN(longitude)) {
    return SearchHospitalService.initializeUserCoordinate();
  }

  return { latitude, longitude };
};

export const initializeSelectedInfluenzaFilters = () => {
  return (Object.values(HospitalInfluenzaFilter) as HospitalInfluenzaFilter[]).reduce(
    (acc, cur) => {
      switch (cur) {
        case HospitalInfluenzaFilter.FreeInfluenza: {
          acc[cur] = new FreeInfluenzaFilter(undefined);
          return acc;
        }
        default:
          return acc;
      }
    },
    {} as {
      [K in HospitalInfluenzaFilter]: InfluenzaFilterType<K>;
    }
  );
};

export const initializeIsInfluenzaFiltersOpen = () => {
  return (Object.values(HospitalInfluenzaFilter) as HospitalInfluenzaFilter[]).reduce(
    (acc, cur) => {
      acc[`${cur}`] = false;
      return acc;
    },
    {} as {
      [K in HospitalInfluenzaFilter]: boolean;
    }
  );
};

export const initializeHospitalSearchCommonFilter = (): HospitalSearchCommonFilter => {
  const initHospitalSearchCommonFilter = (Object.values(HospitalCommonFilterType) as HospitalCommonFilterType[]).reduce(
    (acc, cur) => {
      acc[`${cur}`] = false;
      return acc;
    },
    {} as {
      [K in HospitalCommonFilterType]: boolean;
    }
  );
  return new HospitalSearchCommonFilter(initHospitalSearchCommonFilter);
};

export const getHospitalCommonFilter = (commonFilterQueryString?: string): HospitalSearchCommonFilter => {
  const parsedCommonFilterList = parseQueryStringToArray(commonFilterQueryString);

  const hospitalCommonFilters = (Object.values(HospitalCommonFilterType) as HospitalCommonFilterType[]).reduce(
    (acc, type) => {
      if (parsedCommonFilterList.includes(type)) {
        acc[`${type}`] = true;
      } else {
        acc[`${type}`] = false;
      }
      return acc;
    },
    {} as {
      [K in HospitalCommonFilterType]: boolean;
    }
  );
  return new HospitalSearchCommonFilter(hospitalCommonFilters);
};

export const getHospitalSearchMode = (mode = ''): HospitalSearchMode => {
  switch (mode) {
    case 'NEAR':
      return HospitalSearchMode.NEAR;
    default:
      return HospitalSearchMode.SEARCH;
  }
};

const HOSPITAL_SEARCH_SORT_TYPE_NAME_MAP: {
  [key in HospitalSearchSortType]: string;
} = {
  [HospitalSearchSortType.ACCURACY]: '관련도 순',
  [HospitalSearchSortType.DISTANCE]: '가까운 순',
  [HospitalSearchSortType.REVIEW_AVERAGE_RATING]: '평점 높은 순',
  [HospitalSearchSortType.REVIEW_COUNT]: '리뷰 많은 순',
};
const HOSPITAL_SEARCH_SORT_TYPE_DTO_MAP: {
  [key in HospitalSearchSortType]: HospitalSortType;
} = {
  [HospitalSearchSortType.ACCURACY]: HospitalSortType.Accuracy,
  [HospitalSearchSortType.DISTANCE]: HospitalSortType.Distance,
  [HospitalSearchSortType.REVIEW_AVERAGE_RATING]: HospitalSortType.ReviewAverageRating,
  [HospitalSearchSortType.REVIEW_COUNT]: HospitalSortType.ReviewCount,
};

export class HospitalSortFilter implements EnumCommonFilter<HospitalSearchSortType, HospitalSortType> {
  readonly type;
  constructor(type: HospitalSearchSortType) {
    this.type = type;
  }

  getTitle() {
    return HOSPITAL_SEARCH_SORT_TYPE_NAME_MAP[this.type];
  }
  get dtoType() {
    return HOSPITAL_SEARCH_SORT_TYPE_DTO_MAP[this.type];
  }
  isSelected(selectedType?: HospitalSearchSortType) {
    return this.type === selectedType;
  }
}

const FREE_INFLUENZA_TYPE_NAME_MAP: {
  [key in HospitalFreeInfluenzaType]: string;
} = {
  [HospitalFreeInfluenzaType.Child]: '어린이 무료',
  [HospitalFreeInfluenzaType.Pregnant]: '임신부 무료',
  [HospitalFreeInfluenzaType.Senior]: '노인 무료',
  [HospitalFreeInfluenzaType.SeongnamCitizen]: '성남시민 무료',
};

const FREE_INFLUENZA_TYPE_DTO_MAP: {
  [key in HospitalFreeInfluenzaType]: FreeVaccinationSearchFilterType;
} = {
  [HospitalFreeInfluenzaType.Child]: FreeVaccinationSearchFilterType.Child,
  [HospitalFreeInfluenzaType.Pregnant]: FreeVaccinationSearchFilterType.Pregnant,
  [HospitalFreeInfluenzaType.Senior]: FreeVaccinationSearchFilterType.Senior,
  [HospitalFreeInfluenzaType.SeongnamCitizen]: FreeVaccinationSearchFilterType.SeongnamCitizen,
};

export class FreeInfluenzaFilter
  implements EnumCommonFilter<HospitalFreeInfluenzaType | undefined, FreeVaccinationSearchFilterType | undefined>
{
  constructor(readonly type: HospitalFreeInfluenzaType | undefined) {}

  getTitle() {
    if (!this.type) {
      return '전체';
    }

    return FREE_INFLUENZA_TYPE_NAME_MAP[this.type];
  }

  isSelected(selectedType?: HospitalFreeInfluenzaType) {
    if (!this.type) {
      return false;
    }

    return this.type === selectedType;
  }

  get dtoType() {
    if (!this.type) {
      return;
    }

    return FREE_INFLUENZA_TYPE_DTO_MAP[this.type];
  }
}

enum PartnershipTemplate {
  Partner = 'partner',
  NonPartner = 'nonPartner',
}

const PARTNERSHIP_TEMPLATE_NAME_MAP = {
  [PartnershipTemplate.Partner]: 'partner',
  [PartnershipTemplate.NonPartner]: 'nonPartner',
};

export class HospitalPartnership {
  private isPartner;

  constructor(isPartner: boolean) {
    this.isPartner = isPartner;
  }

  getPartnershipAsString() {
    return this.isPartner
      ? PARTNERSHIP_TEMPLATE_NAME_MAP[PartnershipTemplate.Partner]
      : PARTNERSHIP_TEMPLATE_NAME_MAP[PartnershipTemplate.NonPartner];
  }
  isPartnership() {
    return this.isPartner;
  }
}

const HOSPITAL_COMMON_FILTER_NAME_MAP: {
  [key in HospitalCommonFilterType]: string;
} = {
  [HospitalCommonFilterType.Specialist]: '전문의',
  [HospitalCommonFilterType.Operating]: '진료중',
  [HospitalCommonFilterType.Bookable]: '간편예약',
  [HospitalCommonFilterType.NightOperation]: '야간진료',
  [HospitalCommonFilterType.NonF2f]: '비대면 진료',
  [HospitalCommonFilterType.SundayOperation]: '일요일진료',
};

export class HospitalCommonFilter implements EnumCommonFilter<HospitalCommonFilterType, undefined> {
  constructor(public type: HospitalCommonFilterType) {}
  getTitle(): string {
    return HOSPITAL_COMMON_FILTER_NAME_MAP[this.type];
  }
  get dtoType() {
    return undefined;
  }
  isSelected(selectedType?: HospitalCommonFilterType | undefined): boolean {
    return this.type === selectedType;
  }
}

export class HospitalSearchCommonFilter {
  constructor(public values: HospitalSearchCommonFilterMap) {}
  getTitle(key: keyof HospitalSearchCommonFilterMap): string {
    return HOSPITAL_COMMON_FILTER_NAME_MAP[key];
  }
  isSelected(key: keyof HospitalSearchCommonFilterMap): boolean {
    return this.values[key];
  }
}

export const getAvailableDoctorTimeSlotListMap = ({
  allDoctorBookableTimes = [],
  doctorUnavailableTimes = [],
  hospitalDoctorsByDepartment,
  departmentGroupId,
}: {
  allDoctorBookableTimes?: DoctorBookingTime[];
  doctorUnavailableTimes?: DoctorBookingTime[];
  hospitalDoctorsByDepartment: Record<string, HospitalDoctorResponse[]>;
  departmentGroupId?: string;
}) => {
  const allDoctorListMap =
    allDoctorBookableTimes.reduce<BookingsMap>((bookingTimeMap, currentBookingTime) => {
      currentBookingTime.bookingTimes.forEach((bookingTime) => {
        const foundDoctor = hospitalDoctorsByDepartment[`${departmentGroupId}`]?.find(
          (doctor) => doctor.id === currentBookingTime.doctor.doctorId
        );

        if (!foundDoctor) {
          return bookingTimeMap;
        }

        const bookingTimeInfo = bookingTimeMap.get(bookingTime.startTimeSlotValue) ?? {
          doctors: [],
          bookingTime,
        };

        const updatedBookingTimeInfo = {
          ...bookingTimeInfo,
          doctors: [...bookingTimeInfo.doctors, foundDoctor],
        };

        bookingTimeMap.set(bookingTime.startTimeSlotValue, updatedBookingTimeInfo);
      });
      return bookingTimeMap;
    }, new Map()) ?? (new Map() as BookingsMap);

  const filteredTimeSlotDoctorListMap =
    doctorUnavailableTimes.reduce<BookingsMap>((bookingTimeMap, currentBookingTime) => {
      currentBookingTime.bookingTimes.forEach((bookingTime) => {
        const getBookingTimeInfo = bookingTimeMap.get(bookingTime.startTimeSlotValue) ?? {
          doctors: [],
          bookingTime,
        };

        const filteredBookingTimeInfo = {
          ...getBookingTimeInfo,
          doctors: getBookingTimeInfo.doctors.filter((doctor) => doctor.id !== currentBookingTime.doctor.doctorId),
        };

        bookingTimeMap.set(bookingTime.startTimeSlotValue, filteredBookingTimeInfo);
      });
      return bookingTimeMap;
    }, allDoctorListMap) ?? (new Map() as BookingsMap);

  return filteredTimeSlotDoctorListMap;
};
